import React, { useState } from "react";
import dayjs from "dayjs";

// MUI
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

// Icons
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

// Date Picker
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import "dayjs/locale/en-gb";

// My Files
import firebase from "../../../firebase";
import { makeid } from "../../../utility";

export default function ShareLinkDialog(props) {
    const { open, onClose, file } = props;

    // Form state
    const [expiryOption, setExpiryOption] = useState("24h");
    const [customDateTime, setCustomDateTime] = useState(dayjs().add(1, "day"));
    const [usePassword, setUsePassword] = useState(false);
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    // UI state
    const [isCreating, setIsCreating] = useState(false);
    const [shareLink, setShareLink] = useState(null);
    const [copied, setCopied] = useState(false);

    function calculateExpiryDate() {
        const now = dayjs();
        switch (expiryOption) {
            case "1h":
                return now.add(1, "hour");
            case "24h":
                return now.add(24, "hour");
            case "7d":
                return now.add(7, "day");
            case "30d":
                return now.add(30, "day");
            case "never":
                return now.add(100, "year");
            case "custom":
                return customDateTime;
            default:
                return now.add(24, "hour");
        }
    }

    function isNeverExpires() {
        const expiryDate = calculateExpiryDate();
        const ninetyYearsFromNow = dayjs().add(90, "year");
        return expiryDate.isAfter(ninetyYearsFromNow);
    }

    function handleCancel() {
        resetState();
        onClose();
    }

    function handleClose() {
        resetState();
        onClose();
    }

    function resetState() {
        setExpiryOption("24h");
        setCustomDateTime(dayjs().add(1, "day"));
        setUsePassword(false);
        setPassword("");
        setShowPassword(false);
        setIsCreating(false);
        setShareLink(null);
        setCopied(false);
    }

    async function handleCreateLink() {
        setIsCreating(true);

        try {
            const linkId = makeid(20);
            const expiresAt = calculateExpiryDate().toISOString();
            const isVideo = file.type?.startsWith("video/") || false;

            // Write public share link data
            const shareLinkData = {
                fileKey: file.key,
                fileName: file.displayName || file.filename,
                fileType: file.type,
                isVideo: isVideo,
                expiresAt: expiresAt,
                hasPassword: usePassword && password.length > 0,
                createdBy: firebase.auth().currentUser?.uid || null,
                createdAt: dayjs().toISOString(),
            };

            await firebase
                .database()
                .ref("share_links")
                .child(linkId)
                .set(shareLinkData);

            // If password is set, write to secrets (write-only from client)
            if (usePassword && password.length > 0) {
                await firebase
                    .database()
                    .ref("share_link_secrets")
                    .child(linkId)
                    .set({ password: password });
            }

            // Generate the share URL
            const shareUrl = `${window.location.origin}/share/${linkId}`;
            setShareLink(shareUrl);
        } catch (error) {
            console.error("Error creating share link:", error);
            // You could add error state/snackbar here if desired
        } finally {
            setIsCreating(false);
        }
    }

    function handleCopyLink() {
        if (shareLink) {
            navigator.clipboard.writeText(shareLink);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    }

    function handleExpiryChange(event, newValue) {
        if (newValue !== null) {
            setExpiryOption(newValue);
        }
    }

    // Success state - show link and copy button
    if (shareLink) {
        return (
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                aria-labelledby="share-link-success-title"
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 3,
                            overflow: "hidden",
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        px: { xs: 2, sm: 3 },
                        py: 2.5,
                    }}
                >
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 2,
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                            color: "primary.main",
                            backgroundColor: "rgba(0, 149, 61, 0.09)",
                        }}
                    >
                        <CheckCircleOutlineIcon />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            id="share-link-success-title"
                            variant="h5"
                            component="h2"
                            sx={{ lineHeight: 1.1 }}
                        >
                            Public link created
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                        >
                            The link is ready to share.
                        </Typography>
                    </Box>
                    <IconButton
                        aria-label="Close public link"
                        onClick={handleClose}
                        sx={{ mt: -0.75, mr: -1 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <DialogContent
                    dividers
                    sx={{
                        p: { xs: 2, sm: 2.5 },
                        backgroundColor: "background.default",
                    }}
                >
                    <Box
                        sx={{
                            p: { xs: 1.5, sm: 2 },
                            borderRadius: 2.5,
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                        }}
                    >
                        <Typography
                            variant="subtitle2"
                            sx={{ mb: 0.5, fontWeight: 800 }}
                        >
                            Share this link
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1.5 }}
                        >
                            Anyone with the link can access this file
                            {usePassword
                                ? " after entering the password you set."
                                : "."}
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                p: 1,
                                pl: 1.5,
                                borderRadius: 2,
                                backgroundColor: "background.subtle",
                                border: "1px solid",
                                borderColor: "divider",
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    flexGrow: 1,
                                    minWidth: 0,
                                    wordBreak: "break-all",
                                    fontFamily: "monospace",
                                }}
                            >
                                {shareLink}
                            </Typography>
                            <IconButton
                                aria-label="Copy public link"
                                onClick={handleCopyLink}
                                color={copied ? "success" : "default"}
                                sx={{
                                    flexShrink: 0,
                                    backgroundColor: "background.paper",
                                    border: "1px solid",
                                    borderColor: "divider",
                                    "&:hover": {
                                        backgroundColor: "background.paper",
                                    },
                                }}
                            >
                                {copied ? <CheckIcon /> : <ContentCopyIcon />}
                            </IconButton>
                        </Box>

                        {!isNeverExpires() && (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                    mt: 1.5,
                                    color: "text.secondary",
                                }}
                            >
                                <ScheduleOutlinedIcon fontSize="small" />
                                <Typography variant="body2">
                                    Expires{" "}
                                    {dayjs(calculateExpiryDate()).format(
                                        "DD MMM YYYY [at] HH:mm",
                                    )}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
                    <Button
                        onClick={handleClose}
                        variant="contained"
                        disableElevation
                    >
                        Done
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    // Form state - configure link options
    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            maxWidth="sm"
            fullWidth
            aria-labelledby="share-link-dialog-title"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 3,
                        overflow: "hidden",
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        color: "primary.main",
                        backgroundColor: "rgba(0, 149, 61, 0.09)",
                    }}
                >
                    <PublicOutlinedIcon />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        id="share-link-dialog-title"
                        variant="h5"
                        component="h2"
                        sx={{ lineHeight: 1.1 }}
                    >
                        Create public download link
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            mt: 0.5,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {file?.displayName || file?.filename}
                    </Typography>
                </Box>
                <IconButton
                    aria-label="Close public link"
                    onClick={handleCancel}
                    sx={{ mt: -0.75, mr: -1 }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent
                dividers
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    backgroundColor: "background.default",
                }}
            >
                <Box
                    sx={{
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: 2.5,
                        border: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        Link expiry
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.25, mb: 1.5 }}
                    >
                        Choose how long the public link should remain active.
                    </Typography>
                    <ToggleButtonGroup
                        value={expiryOption}
                        exclusive
                        onChange={handleExpiryChange}
                        size="small"
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "repeat(2, minmax(0, 1fr))",
                                sm: "repeat(3, minmax(0, 1fr))",
                            },
                            gap: 0.75,
                            "& .MuiToggleButtonGroup-grouped": {
                                m: 0,
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: "8px !important",
                                textTransform: "none",
                                fontWeight: 700,
                            },
                            "& .Mui-selected": {
                                color: "primary.dark",
                                backgroundColor:
                                    "rgba(0, 149, 61, 0.10) !important",
                                borderColor:
                                    "rgba(0, 149, 61, 0.35) !important",
                            },
                        }}
                    >
                        <ToggleButton value="1h">1 hour</ToggleButton>
                        <ToggleButton value="24h">24 hours</ToggleButton>
                        <ToggleButton value="7d">7 days</ToggleButton>
                        <ToggleButton value="30d">30 days</ToggleButton>
                        <ToggleButton value="custom">Custom</ToggleButton>
                        <ToggleButton value="never">Never</ToggleButton>
                    </ToggleButtonGroup>

                    {expiryOption === "custom" && (
                        <Box sx={{ mt: 1.5 }}>
                            <LocalizationProvider
                                dateAdapter={AdapterDayjs}
                                adapterLocale="en-gb"
                            >
                                <DateTimePicker
                                    label="Expiry date and time"
                                    value={customDateTime}
                                    onChange={(newValue) =>
                                        setCustomDateTime(newValue)
                                    }
                                    minDateTime={dayjs()}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: "small",
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Box>
                    )}
                </Box>

                <Box
                    sx={{
                        mt: 1.5,
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: 2.5,
                        border: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.25,
                        }}
                    >
                        <Box
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: 1.75,
                                display: "grid",
                                placeItems: "center",
                                flexShrink: 0,
                                color: "text.secondary",
                                backgroundColor: "background.subtle",
                            }}
                        >
                            <LockOutlinedIcon fontSize="small" />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 800 }}
                            >
                                Password protection
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Require a password before the file can be
                                accessed.
                            </Typography>
                        </Box>
                    </Box>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={usePassword}
                                onChange={(e) =>
                                    setUsePassword(e.target.checked)
                                }
                            />
                        }
                        label="Require password"
                        sx={{ mt: 1, mb: usePassword ? 0.5 : 0 }}
                    />

                    {usePassword && (
                        <TextField
                            fullWidth
                            size="small"
                            label="Password"
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label={
                                                showPassword
                                                    ? "Hide password"
                                                    : "Show password"
                                            }
                                            onClick={() =>
                                                setShowPassword(!showPassword)
                                            }
                                            edge="end"
                                            size="small"
                                        >
                                            {showPassword ? (
                                                <VisibilityOff />
                                            ) : (
                                                <Visibility />
                                            )}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
                <Button onClick={handleCancel} color="inherit">
                    Cancel
                </Button>
                <Button
                    onClick={handleCreateLink}
                    disabled={usePassword && password.length === 0}
                    variant="contained"
                    disableElevation
                >
                    Create link
                </Button>
            </DialogActions>

            {isCreating && <LinearProgress />}
        </Dialog>
    );
}
